import {createNanoEvents} from 'nanoevents'

import {setErrorMetadata} from './errors'
import {logger} from './logger'
import OriginParser from './origin-parser'

const Logger = logger('iframe')

const READY_STATUS = 'ready'
const HELLO_STATUS = 'hello'
const MESSAGE_TYPE = 'render'
const RENDER_COMMAND = 'render:cmd'
const ACK = 'ack'
const HANDSHAKE_INTERVAL = 1000

const originParser = new OriginParser()
let attempts = 10

/**
 * Dotcom provides a hash with each request to a our code rendering services to
 * ensure that messages are coming from the source it expects
 * @returns string
 */
function getIdentity() {
  return window.location.hash.substring(1)
}

function getMessageContents(data: string) {
  try {
    const payload = JSON.parse(data)
    return {
      type: payload.type,
      identity: payload.identity,
      body: payload.body,
    }
  } catch (error) {
    Logger.debug('Error parsing message data.', error)
    return {}
  }
}

export interface IframeMessengerAPI {
  init(): void
  post(status: string, payload: Record<string, unknown>): void
  didAck(): boolean
  on(eventName: string, callback: (payload: unknown) => void): void
}

class NullIframeMessenger implements IframeMessengerAPI {
  init() {
    return
  }
  post() {
    return
  }
  didAck() {
    return false
  }
  on() {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return () => {}
  }
}

export class IframeMessenger implements IframeMessengerAPI {
  #listening = false
  #initialAck = false
  #handshakeInterval: false | ReturnType<typeof setTimeout>
  #eventBus = createNanoEvents()

  constructor() {
    this.#listening = false
    this.#initialAck = false
    this.#handshakeInterval = false
  }

  #receive(event: MessageEvent) {
    const {data, origin} = event
    setErrorMetadata('origin', origin)
    setErrorMetadata('originMessage', JSON.stringify(data))
    // Sometimes the window posts empty messages to itself. We don't want to
    // process those and they throw errors.
    const subdomainIsolated: string =
      document.getElementById('notebook-subdomain-isolated')?.getAttribute('data-notebook-subdomain-isolated') ||
      'false'

    if (subdomainIsolated === 'true' && origin === window.location.origin) {
      Logger.debug('Window posting to itself.')
      return
    }

    if (!data || !origin) {
      Logger.debug('No data or message origin received.')
      return
    }

    if (!originParser.match(origin)) {
      Logger.debug('Invalid message origin received.')
      return
    }

    const {type, body} = getMessageContents(data)

    // dotcom posts random third party axe checks to other iframes, ignore those
    if (type !== RENDER_COMMAND || !body) {
      return
    }

    if (body.cmd === ACK) {
      this.post(READY_STATUS)
      this.#initialAck = true
    } else {
      this.#eventBus.emit(body.cmd, body.payload)
    }
  }

  init() {
    if (this.#listening) {
      Logger.debug('Already initialized')
      return
    }

    this.#listening = true

    window.addEventListener('message', event => {
      this.#receive(event)
    })

    this.#handshakeInterval = setInterval(() => {
      if (this.#handshakeInterval !== false && (this.#initialAck || attempts === 0)) {
        clearInterval(this.#handshakeInterval)
        return
      }

      this.post(HELLO_STATUS, {})
      attempts -= 1
    }, HANDSHAKE_INTERVAL)
  }

  post(status: string, payload = {}) {
    const message = {
      type: MESSAGE_TYPE,
      body: status,
      payload,
      when: Date.now(),
      identity: getIdentity(),
    }

    window.parent.postMessage(message, '*')
  }

  didAck() {
    return this.#initialAck
  }

  on(eventName: string, callback: (payload: unknown) => void) {
    return this.#eventBus.on(eventName, callback)
  }
}

// The current window and parent window are the same, we are not in an iframe
export function isNotIframeContext() {
  return window.parent === window
}

let iframeMessage: IframeMessengerAPI
export function ensureIframeMessenger() {
  if (isNotIframeContext()) {
    return (iframeMessage ||= new NullIframeMessenger())
  }

  return (iframeMessage ||= new IframeMessenger())
}
